import io from 'socket.io-client';

class RealtimeService {
  constructor() {
    this.socket = null;
    this.isConnected = false;
    this.listeners = new Map();
    this.currentListId = null;
    
    // Simple mobile handlers
    this.setupMobileHandlers();
  }

  setupMobileHandlers() {
    // Handle page visibility changes (mobile browsers suspending/resuming)
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        console.log('🔄 Page became visible, checking connection...');
        setTimeout(() => {
          if (!this.isConnected) {
            console.log('🔄 Not connected, attempting reconnect...');
            this.reconnect();
          }
          // Always trigger a refresh when coming back from background
          this.emit('need-refresh', { reason: 'page-visible' });
        }, 1000);
      }
    });

    // Handle focus events
    window.addEventListener('focus', () => {
      console.log('🔄 Window focused');
      setTimeout(() => {
        if (!this.isConnected) {
          this.reconnect();
        }
        this.emit('need-refresh', { reason: 'window-focus' });
      }, 500);
    });
  }

  connect() {
    if (this.socket) {
      console.log('🔄 Socket already exists, not reconnecting');
      return;
    }

    // Get base URL from environment variables
    const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || 'http://10.41.0.186:3001/api';
    console.log('🔄 API Base URL from env:', apiBaseUrl);
    console.log('🔄 Environment variables:', {
      REACT_APP_API_BASE_URL: process.env.REACT_APP_API_BASE_URL,
      NODE_ENV: process.env.NODE_ENV
    });
    
    let serverUrl;
    
    // Handle different environments
    if (apiBaseUrl.startsWith('/api')) {
      // Production: relative URL, use current origin
      serverUrl = window.location.origin;
      console.log('🔄 Production mode: Using current origin for WebSocket:', serverUrl);
    } else {
      // Development: absolute URL, extract server URL
      serverUrl = apiBaseUrl.replace('/api', '');
      console.log('🔄 Development mode: Using extracted server URL for WebSocket:', serverUrl);
    }
    
    console.log('🔄 Attempting to connect to real-time server:', serverUrl);

    // Connect to the Socket.IO server
    this.socket = io(serverUrl, {
      transports: ['websocket', 'polling'], // Add polling as fallback
      upgrade: true,
      timeout: 10000,
      forceNew: true
    });

    this.socket.on('connect', () => {
      console.log('🔄 Real-time connection established to:', serverUrl);
      this.isConnected = true;
      this.emit('connection-changed', true);
      
      // Rejoin current list if we were connected to one
      if (this.currentListId) {
        this.joinList(this.currentListId);
      }
    });

    this.socket.on('disconnect', (reason) => {
      console.log('🔄 Real-time connection lost from:', serverUrl, 'reason:', reason);
      this.isConnected = false;
      this.emit('connection-changed', false);
    });

    this.socket.on('connect_error', (error) => {
      console.error('🔄 Real-time connection error to:', serverUrl);
      console.error('🔄 Error details:', error);
      this.isConnected = false;
      this.emit('connection-changed', false);
    });

    this.socket.on('reconnect', (attemptNumber) => {
      console.log('🔄 Real-time reconnected after', attemptNumber, 'attempts');
    });

    this.socket.on('reconnect_error', (error) => {
      console.error('🔄 Real-time reconnection error:', error);
    });

    // Handle pong responses
    this.socket.on('pong', () => {
      console.log('🔄 Received pong, connection is alive');
    });

    // Listen for list events
    this.socket.on('item-added', (data) => {
      console.log('📝 Item added:', data);
      this.emit('item-added', data);
    });

    this.socket.on('item-updated', (data) => {
      console.log('✏️ Item updated:', data);
      this.emit('item-updated', data);
    });

    this.socket.on('item-deleted', (data) => {
      console.log('🗑️ Item deleted:', data);
      this.emit('item-deleted', data);
    });

    this.socket.on('list-cleared', (data) => {
      console.log('🧹 List cleared:', data);
      this.emit('list-cleared', data);
    });

    this.socket.on('list-unchecked', (data) => {
      console.log('🔄 List unchecked:', data);
      this.emit('list-unchecked', data);
    });

    this.socket.on('list-blocked', (data) => {
      console.log('🚫 List blocked/unblocked:', data);
      this.emit('list-blocked', data);
    });

    this.socket.on('list-ownership-transferred', (data) => {
      console.log('👑 List ownership transferred:', data);
      this.emit('list-ownership-transferred', data);
    });

    this.socket.on('list-created', (data) => {
      console.log('📝 List created:', data);
      this.emit('list-created', data);
    });

    this.socket.on('list-shared', (data) => {
      console.log('🤝 List shared:', data);
      this.emit('list-shared', data);
    });

    this.socket.on('list-unshared', (data) => {
      console.log('🚫 List unshared:', data);
      this.emit('list-unshared', data);
    });
  }

  reconnect() {
    console.log('🔄 Attempting to reconnect...');
    
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }
    
    this.isConnected = false;
    this.connect();
  }

  disconnect() {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
      this.isConnected = false;
      this.currentListId = null;
    }
  }

  joinList(listId) {
    if (!this.socket || !this.isConnected) {
      console.warn('Cannot join list: not connected to real-time server');
      return;
    }

    // Leave previous list if we were in one
    if (this.currentListId && this.currentListId !== listId) {
      this.socket.emit('leave-list', this.currentListId);
    }

    // Join new list
    this.currentListId = listId;
    this.socket.emit('join-list', listId);
    console.log(`🔄 Joined real-time updates for list ${listId}`);
  }

  leaveList(listId) {
    if (!this.socket || !this.isConnected) {
      return;
    }

    this.socket.emit('leave-list', listId);
    if (this.currentListId === listId) {
      this.currentListId = null;
    }
    console.log(`🔄 Left real-time updates for list ${listId}`);
  }

  // Event listener management
  on(event, callback) {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    this.listeners.get(event).add(callback);
  }

  off(event, callback) {
    if (this.listeners.has(event)) {
      this.listeners.get(event).delete(callback);
    }
  }

  emit(event, data) {
    if (this.listeners.has(event)) {
      this.listeners.get(event).forEach(callback => {
        try {
          callback(data);
        } catch (error) {
          console.error('Error in real-time event callback:', error);
        }
      });
    }
  }

  // Get connection status
  getConnectionStatus() {
    return {
      isConnected: this.isConnected,
      currentListId: this.currentListId
    };
  }
}

// Create a singleton instance
const realtimeService = new RealtimeService();

export default realtimeService;
